import { create } from "zustand";
const CACHE_BUSTER_STORAGE_KEY = "mediashare_cache_buster";
// Helper functions for localStorage operations
const loadCacheBusterFromStorage = () => {
    try {
        // Check if localStorage is available (not available in Jest test environment)
        if (typeof localStorage === "undefined") {
            return { offset: 0, counter: 0, active: false };
        }
        const stored = localStorage.getItem(CACHE_BUSTER_STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            const now = Date.now();
            const stored24HoursAgo = now - 24 * 60 * 60 * 1000;
            // Check if data is older than 24 hours
            if (parsed.timestamp && parsed.timestamp < stored24HoursAgo) {
                localStorage.removeItem(CACHE_BUSTER_STORAGE_KEY);
                return { offset: 0, counter: 0, active: false };
            }
            return {
                offset: parsed.offset || 0,
                counter: parsed.counter || 0,
                active: parsed.active || false,
            };
        }
    }
    catch (error) {
        console.warn("Failed to load cache buster from localStorage:", error);
    }
    return { offset: 0, counter: 0, active: false };
};
const saveCacheBusterToStorage = (offset, counter, active) => {
    try {
        // Check if localStorage is available (not available in Jest test environment)
        if (typeof localStorage === "undefined") {
            return;
        }
        localStorage.setItem(CACHE_BUSTER_STORAGE_KEY, JSON.stringify({
            offset,
            counter,
            active,
            timestamp: Date.now(),
        }));
    }
    catch (error) {
        console.warn("Failed to save cache buster to localStorage:", error);
    }
};
export const useCacheBusterStore = create()((set, get) => {
    // Initialize from localStorage
    const initialData = loadCacheBusterFromStorage();
    return {
        cacheBusterCounter: initialData.counter,
        cacheBusterActive: initialData.active,
        isCacheBusterActive: () => {
            return get().cacheBusterActive;
        },
        initializeCacheBuster: () => {
            console.log("initializeCacheBuster - setting to 0");
            const newCounter = 0;
            const newActive = false;
            // Save to localStorage
            saveCacheBusterToStorage(0, newCounter, newActive);
            // Update state
            set({
                cacheBusterCounter: newCounter,
                cacheBusterActive: newActive,
            });
            console.log(`Cache buster initialized: counter=${newCounter}, active=${newActive}`);
        },
        incrementCacheBuster: () => {
            console.log("incrementCacheBuster - incrementing by 1");
            const state = get();
            const newActive = true;
            const newCounter = (state.cacheBusterCounter || 0) + 1;
            // Save to localStorage
            saveCacheBusterToStorage(0, newCounter, newActive);
            // Update state
            set({
                cacheBusterCounter: newCounter,
                cacheBusterActive: newActive,
            });
            console.log(`Cache buster incremented: counter=${newCounter}, active=${newActive}`);
        },
        getCacheBusterNumber: () => {
            const state = get();
            if (!state.cacheBusterActive) {
                // When not active, return 0
                return 0;
            }
            // Return the counter value
            return state.cacheBusterCounter;
        },
        getCacheBuster: () => {
            const state = get();
            return "" + state.getCacheBusterNumber();
        },
    };
});
export const authUrl = (url, cacheBusterStore, auth, logger) => {
    if (logger) {
        logger.log("authUrl: input url = " + url);
    }
    if (auth &&
        auth.isSignedIn(logger) &&
        auth.bearer &&
        url &&
        !url.includes("bearer") &&
        !url.startsWith("blob") &&
        !url.startsWith("data:")) {
        // Apply cache buster to all authenticated URLs
        const retVal = url +
            "?bearer=" +
            encodeURIComponent(auth.bearer) +
            "&cb=" +
            cacheBusterStore.getCacheBuster();
        if (logger) {
            logger.log("URL w/ bearer and cache buster = " + retVal);
        }
        return retVal;
    }
    return url;
};
export const authUrlWithLocalCacheBuster = (url, cacheBusterStore, localCacheBuster, auth, logger) => {
    if (logger) {
        logger.log("authUrlWithLocalCacheBuster: input url = " +
            url +
            ", localCacheBuster = " +
            localCacheBuster);
    }
    if (auth &&
        auth.isSignedIn(logger) &&
        auth.bearer &&
        url &&
        !url.includes("bearer") &&
        !url.startsWith("blob") &&
        !url.startsWith("data:")) {
        // If the URL matches the pattern of a post, then don't include the cache buster.
        // Post pattern is u/<uuid>/<uuid>/<timestamp>-<uuid>/*
        const postPattern = /.*u\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/\d{13}-[0-9a-f]{8}.*/;
        const isPost = postPattern.test(url);
        // Check if URL is for HLS content (.m3u8 or .ts files) and exclude cache buster
        const hlsPattern = /\.(m3u8|ts)(\?|$)/i;
        const isHLS = hlsPattern.test(url);
        if (logger) {
            logger.log("isPost = " + isPost + " for url = " + url);
            logger.log("isHLS = " + isHLS + " for url = " + url);
        }
        // Exclude cache buster for posts and HLS content
        const excludeCacheBuster = isPost || isHLS;
        let retVal = url + "?bearer=" + encodeURIComponent(auth.bearer);
        // Add global cache buster if not excluded
        if (!excludeCacheBuster) {
            retVal += "&cb=" + cacheBusterStore.getCacheBuster();
        }
        // Add local cache buster if provided (for polling)
        if (localCacheBuster !== undefined) {
            retVal += "&poll=" + localCacheBuster;
        }
        if (logger) {
            logger.log("URL w/ bearer and cache busters = " + retVal);
        }
        return retVal;
    }
    return url;
};
